import { AsyncPipe, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { COMPANY_API_SERVICE_TOKEN, COMPANY_EVENT_API_SERVICE_TOKEN, PERSON_API_SERVICE_TOKEN } from '@nk/api-lib';
import { CompanyFlagCompositeEntity, EventEntity } from '@nk/data-entities-lib';
import { SpinnerComponent, TranslateFallbackPipe } from '@nk/ui-lib';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { EMPTY, forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';
import {
  AccountsFlagContext,
  CollateralFlagContext,
  CompanyFlagContext,
  EntityFlagContext,
  EventFlagContext,
  ExternalUrlFlagContext,
  isAccountsContext,
  isCollateralContext,
  isEntityContext,
  isEventContext,
  isExternalUrlContext,
} from './company-flag-context.utils';

export interface ContextListLink {
  title: string;
  underTitle?: string | number | null;
  internalUrl?: string;
  externalUrl?: string;
}

@Component({
  selector: 'nk-company-flag-context-content',
  templateUrl: './company-flag-context-content.component.html',
  styleUrls: ['./company-flag-context-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
  imports: [
    AsyncPipe,
    DatePipe,
    TranslateModule,
    RouterLink,
    TranslateFallbackPipe,
    SpinnerComponent,
    PopoverModule,
  ],
})
export class CompanyFlagContextContentComponent implements OnInit {
  public companyFlagComposite = input.required<CompanyFlagCompositeEntity>();

  /** Show the evidence list (linked persons, events, etc.) */
  public showEvidence = input(true);

  /** Show the flag type description info box */
  public showDescription = input(true);

  /** Show the flag type name as a header */
  public showHeader = input(false);

  /** Use compact layout (less spacing, smaller text) */
  public compact = input(false);

  /** Show the flag date */
  public showDate = input(false);

  public contextListLinks: Observable<ContextListLink>[] = [];
  public remainingCount = 0;
  public usePopover = false;

  private _destroy = inject(DestroyRef);
  private _personService = inject(PERSON_API_SERVICE_TOKEN);
  private _companyEventService = inject(COMPANY_EVENT_API_SERVICE_TOKEN);
  private _companyService = inject(COMPANY_API_SERVICE_TOKEN);
  private _translateService = inject(TranslateService);
  private _datePipe = inject(DatePipe);

  public ngOnInit(): void {
    const contexts = this._parseFlagContexts(this.companyFlagComposite());
    this.contextListLinks = contexts.map((ctx) => this._resolveContext(ctx));

    // Cache results and filter out null emissions so spinners don't hang
    this.contextListLinks = this.contextListLinks
      .filter(Boolean)
      .map((obs) => {
        const subject = new ReplaySubject<ContextListLink>(1);
        obs.pipe(
          filter((value): value is ContextListLink => value != null),
          takeUntilDestroyed(this._destroy),
        ).subscribe(subject);
        return subject.asObservable();
      });

    // Show up to 2 items inline; use popover only when there are 3+
    this.usePopover = this.contextListLinks.length > 2;
    const maxInlineItems = this.usePopover ? 1 : 2;
    this.remainingCount = Math.max(0, this.contextListLinks.length - maxInlineItems);
  }

  private _parseFlagContexts(composite: CompanyFlagCompositeEntity): CompanyFlagContext[] {
    const raw = composite.company_flag.flag_context;
    if (!raw) {
      return [];
    }
    const flags: CompanyFlagContext[] = Array.isArray(raw) ? raw : [raw];
    return flags
      .filter((f, i, arr) => {
        if (!isEntityContext(f) || !f.uuid) return true;
        return arr.findIndex((other) => isEntityContext(other) && other.uuid === f.uuid) === i;
      })
      .map((f) => {
        if (isEntityContext(f)) {
          return { ...f, entity_key: this._fixLegacyEntityNames(f.entity_key) };
        }
        return f;
      });
  }

  private _resolveContext(ctx: CompanyFlagContext): Observable<ContextListLink> {
    if (isEventContext(ctx)) return this._resolveEventContext(ctx);
    if (isCollateralContext(ctx)) return this._resolveCollateralContext(ctx);
    if (isAccountsContext(ctx)) return this._resolveAccountsContext(ctx);
    if (isExternalUrlContext(ctx)) return this._resolveExternalUrlContext(ctx);
    if (isEntityContext(ctx)) return this._resolveEntityContext(ctx);
    return EMPTY;
  }

  // The dashboard API returns raw flag_context from the DB, which uses a different schema
  // than the standard company flags API. The event_uuid field can refer to either a
  // company_event or an event — there is no way to distinguish from the data alone.
  // We try companyEventCache first and fall back to eventCache on 404.
  private _resolveEventContext(ctx: EventFlagContext): Observable<ContextListLink> {
    return this._companyEventService.companyEventCache.get(ctx.event_uuid).pipe(
      mergeMap((companyEvent) => {
        if (companyEvent.disabled_flag || !companyEvent.event_uuid) {
          return of<ContextListLink>({ title: companyEvent.title_override || '' });
        }
        return this._companyEventService.eventCache.get(companyEvent.event_uuid).pipe(
          map(
            (event): ContextListLink => ({
              title: companyEvent.title_override || event.title,
              internalUrl: `/risk/event-analysis/${ctx.event_uuid}`,
            }),
          ),
        );
      }),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 404) {
          return this._companyEventService.eventCache.get(ctx.event_uuid).pipe(
            map(
              (event): ContextListLink => ({
                title: event.title,
              }),
            ),
          );
        }
        if (error instanceof HttpErrorResponse && error.status === 403) {
          return of<ContextListLink>({
            title: extractTranslation('event.restricted_access_event'),
          });
        }
        throw error;
      }),
    );
  }

  private _resolveAccountsContext(ctx: AccountsFlagContext): Observable<ContextListLink> {
    const companyUuid = this.companyFlagComposite().company?.uuid;
    return of({
      title: extractTranslation('tabs.company.accounts'),
      internalUrl: companyUuid ? `/company/${companyUuid}/accounts` : undefined,
    });
  }

  private _resolveExternalUrlContext(ctx: ExternalUrlFlagContext): Observable<ContextListLink> {
    return of({
      title: ctx.external_url_label || extractTranslation('event.external_link'),
      externalUrl: ctx.external_url,
    });
  }

  private _resolveCollateralContext(ctx: CollateralFlagContext): Observable<ContextListLink> {
    return of({
      title: this._translateService.instant(extractTranslation('accounts.collateral_count'), {
        count: ctx.collateral_count,
      }),
      underTitle: ctx.latest_registration_date ? this._datePipe.transform(ctx.latest_registration_date) : '',
    });
  }

  private _resolveEntityContext(ctx: EntityFlagContext): Observable<ContextListLink> {
    switch (ctx.entity_key) {
      case 'person':
        return this._resolvePersonEntity(ctx.entity_uuid);
      case 'company_event':
        return ctx.entity_uuid ? this._resolveCompanyEventEntity(ctx) : EMPTY;
      case 'company':
        return this._resolveCompanyEntity(ctx.entity_uuid);
      default:
        if (ctx.external_url) {
          return of({
            title: ctx.external_url_label || extractTranslation('event.external_link'),
            externalUrl: ctx.external_url,
          });
        }
        return EMPTY;
    }
  }

  private _resolvePersonEntity(entityUuid: string): Observable<ContextListLink> {
    return this._personService.personMasterByPerson.get(entityUuid).pipe(
      mergeMap((personMaster) => forkJoin([this._personService.personCache.get(entityUuid), of(personMaster)])),
      map(([person, personMaster]) => {
        if (personMaster) {
          return {
            title: person.full_name,
            underTitle: person.birth_year,
            internalUrl: `/person/${personMaster[0].uuid}`,
          };
        } else if (person) {
          return {
            title: person.full_name,
            underTitle: person.birth_year,
          };
        } else {
          return {
            title: extractTranslation('person.unknown_person'),
          };
        }
      }),
    );
  }

  private _resolveCompanyEventEntity(ctx: EntityFlagContext): Observable<ContextListLink> {
    return this._companyEventService.companyEventCache.get(ctx.entity_uuid).pipe(
      mergeMap((companyEvent) => {
        if (companyEvent.disabled_flag || !companyEvent.event_uuid) {
          return forkJoin([of<EventEntity | null>(null), of(companyEvent)]);
        }
        return forkJoin([this._companyEventService.eventCache.get(companyEvent.event_uuid), of(companyEvent)]);
      }),
      map((payload): ContextListLink | undefined => {
        const [event, companyEvent] = payload;
        if (event) {
          return {
            title: companyEvent.title_override || event.title,
            internalUrl: `/risk/event-analysis/${ctx.entity_uuid}`,
          };
        }
        if (ctx.external_url) {
          return {
            title: ctx.external_url_label || extractTranslation('event.external_link'),
            externalUrl: ctx.external_url,
          };
        }
        return undefined;
      }),
      filter((value): value is ContextListLink => value !== undefined),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 404) {
          return this._companyEventService.eventCache.get(ctx.entity_uuid).pipe(
            map(
              (event): ContextListLink => ({
                title: event.title,
              }),
            ),
            catchError(() => this._externalUrlFallback(ctx)),
          );
        }
        if (error instanceof HttpErrorResponse && error.status === 403) {
          if (ctx.external_url) {
            return of<ContextListLink>({
              title: ctx.external_url_label || extractTranslation('event.external_link'),
              externalUrl: ctx.external_url,
            });
          }
          return of<ContextListLink>({
            title: extractTranslation('event.restricted_access_event'),
          });
        }
        throw error;
      }),
    );
  }

  private _externalUrlFallback(ctx: EntityFlagContext): Observable<ContextListLink> {
    if (ctx.external_url) {
      return of<ContextListLink>({
        title: ctx.external_url_label || extractTranslation('event.external_link'),
        externalUrl: ctx.external_url,
      });
    }
    return EMPTY;
  }

  private _resolveCompanyEntity(entityUuid: string): Observable<ContextListLink> {
    return this._companyService.companyCache.get(entityUuid).pipe(
      map((company) => ({
        title: company.name,
        underTitle: `${company.org_nr_schema} ${company.org_nr}`,
        internalUrl: `/company/${company.uuid}`,
      })),
    );
  }

  private _fixLegacyEntityNames(entityKey: string): string {
    const convertEntityName = entityKey && entityKey.indexOf('_') < 0;
    if (convertEntityName) {
      return entityKey
        .replace('Entity', '')
        .split(/(?=[A-Z])/)
        .join('_')
        .toLowerCase();
    }
    return entityKey;
  }
}
